/* Find Community Near You.
 *
 * Two modes, and which one runs depends entirely on whether a real search API
 * has been configured on the section:
 *
 *   1. No endpoint (the default today) — the form hands off to a real public
 *      directory for the chosen kind of help. Nothing is rendered as a result,
 *      because we have no data source and a made-up church is worse than a
 *      handoff.
 *
 *   2. `data-endpoint` set — the form GETs `<endpoint>?location=&kind=` and
 *      renders whatever comes back. Expected shape:
 *        { results: [ { name, distance, address, url, times, groups } ] }
 *      Wire this to a church-directory or places API when one is chosen.
 */
class LearnCommunity extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-location]');
    this.results = this.querySelector('[data-results]');
    this.kind = this.dataset.defaultKind || 'churches';
    this.endpoint = this.dataset.endpoint || '';

    this.querySelectorAll('[data-kind]').forEach((btn) => {
      btn.addEventListener('click', () => this.selectKind(btn));
    });

    this.form?.addEventListener('submit', (e) => this.onSubmit(e));
  }

  selectKind(btn) {
    this.querySelectorAll('[data-kind]').forEach((b) =>
      b.setAttribute('aria-pressed', String(b === btn))
    );
    this.kind = btn.dataset.kind;
  }

  onSubmit(event) {
    event.preventDefault();
    const location = (this.input?.value || '').trim();
    if (!location) {
      this.input?.focus();
      return;
    }
    if (this.endpoint) return this.search(location);
    this.handOff(location);
  }

  /* No endpoint: open a real directory search in a new tab. The only thing that
   * leaves the page is the location the visitor typed, to the map provider they
   * are being sent to. */
  handOff(location) {
    const terms = {
      churches: 'churches',
      counseling: 'christian counselors and therapists',
      support: 'support groups',
      recovery: 'addiction recovery meetings',
      shelter: 'domestic violence shelters and safe housing',
    };
    const query = `${terms[this.kind] || terms.churches} near ${location}`;
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
    window.open(url, '_blank', 'noopener');
    this.announce(`Opening a map search for ${query}.`);
  }

  async search(location) {
    this.announce('Searching…');
    try {
      const url = new URL(this.endpoint, window.location.origin);
      url.searchParams.set('location', location);
      url.searchParams.set('kind', this.kind);
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      this.render(data.results || []);
    } catch {
      /* Never fall back to invented results. Offer the directory instead. */
      this.results.innerHTML =
        '<p class="learn-find__note">We could not load results just now. ' +
        '<button type="button" class="learn-find__kind" data-fallback>Search a map instead</button></p>';
      this.results
        .querySelector('[data-fallback]')
        ?.addEventListener('click', () => this.handOff(location));
    }
  }

  render(results) {
    if (!results.length) {
      this.results.innerHTML =
        '<p class="learn-find__note">Nothing came back for that location. Try a nearby town or a wider area.</p>';
      return;
    }
    const esc = (s) =>
      String(s ?? '').replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
      );

    this.results.innerHTML =
      `<h4 class="learn-eyebrow">${results.length} nearby</h4>` +
      '<ul class="learn-cards learn-cards--2">' +
      results
        .map(
          (r) => `<li class="learn-card learn-resource">
            <h5 class="learn-resource__name">${esc(r.name)}</h5>
            ${r.distance ? `<p class="learn-resource__desc">${esc(r.distance)}</p>` : ''}
            ${r.address ? `<p class="learn-resource__desc">${esc(r.address)}</p>` : ''}
            ${r.times ? `<p class="learn-resource__desc">${esc(r.times)}</p>` : ''}
            ${r.groups ? `<p class="learn-resource__desc">${esc(r.groups)}</p>` : ''}
            ${r.url ? `<p><a href="${esc(r.url)}" rel="noopener">Visit website</a></p>` : ''}
          </li>`
        )
        .join('') +
      '</ul>';
  }

  announce(message) {
    if (this.results) this.results.innerHTML = `<p class="learn-find__note">${message}</p>`;
  }
}

if (!customElements.get('learn-community')) {
  customElements.define('learn-community', LearnCommunity);
}
